/**
 * section-order.js — canonical visual order of the selectable sections on the
 * active page, derived from state.
 *
 * Pure and import-free by design. The selection model needs an ordered id list
 * to resolve ranges against, and there are three tempting-but-wrong sources:
 *
 *   - the sidebar DOM — `SectionList._renderRows()` rebuilds `innerHTML` behind
 *     a 50ms debounce, leaves search-filtered rows in the tree, and renders
 *     nothing before first paint or while the Sections tab is unmounted.
 *   - `iframe:section-positions` — rAF-throttled, scroll-coupled, absent until
 *     the bridge is ready.
 *   - a cached copy — every cache needs invalidating on state-changed, page
 *     switch, undo, collab remote-apply and hydration; each hook is a bug.
 *
 * So: derive on demand from state. The list is tens of items; call it once per
 * gesture. The order here is identical by construction to the sidebar's own
 * composition (nav pinned top, then `page.sections` in array order, then footer)
 * — keep the two in step if either moves.
 */

/**
 * The active page, mirroring `StateStore.getActivePage()` including its
 * fall back to the first page when `activePage` names a page that is gone.
 * Duplicated rather than imported so this module stays dependency-free.
 * @param {object} state
 * @returns {object | undefined}
 */
function activePageOf(state) {
  const pages = state?.pages;
  if (!Array.isArray(pages) || pages.length === 0) return undefined;
  return pages.find((p) => p?.id === state.activePage) || pages[0];
}

/**
 * Instance ids of the active page's own sections, in document order.
 * Shared nav/footer are excluded: they are not page members, cannot be
 * duplicated or reordered, and delete routes through `removeSharedSection`.
 *
 * This is the candidate set for a multi-selection — the invariant being that a
 * selection of two or more contains active-page sections only.
 *
 * @param {object} state
 * @returns {string[]}
 */
export function pageSectionOrder(state) {
  const page = activePageOf(state);
  if (!page || !Array.isArray(page.sections)) return [];
  const ids = [];
  for (const section of page.sections) {
    if (section?.instanceId) ids.push(section.instanceId);
  }
  return ids;
}

/**
 * Every selectable instance id in the order the section list paints them:
 * shared navigation, then the active page's sections, then shared footer.
 *
 * This is the scalar-selection domain — what `prune()` validates against, so a
 * selected nav/footer survives a re-render. Ranges use `pageSectionOrder`.
 *
 * @param {object} state
 * @returns {string[]}
 */
export function sectionOrderForPage(state) {
  const ids = [];
  const nav = state?.sharedSections?.navigation;
  if (nav?.instanceId) ids.push(nav.instanceId);
  ids.push(...pageSectionOrder(state));
  const footer = state?.sharedSections?.footer;
  if (footer?.instanceId) ids.push(footer.instanceId);
  return ids;
}

/**
 * `'navigation' | 'footer'` for a shared section, else `null`.
 *
 * The selection model's group resolver: `extendTo` clamps a range to the
 * anchor's group, which is what keeps nav/footer out of every range without the
 * range code needing to know what a shared section is.
 *
 * Mirrors `sectionManager.getSharedType` but takes state explicitly, so this
 * module stays pure and testable without a store.
 *
 * @param {object} state
 * @param {string} instanceId
 * @returns {'navigation' | 'footer' | null}
 */
export function sharedTypeOf(state, instanceId) {
  if (!instanceId) return null;
  const shared = state?.sharedSections;
  if (shared?.navigation?.instanceId === instanceId) return 'navigation';
  if (shared?.footer?.instanceId === instanceId) return 'footer';
  return null;
}

/**
 * `'shared' | 'page'` — the group key `SelectionStore.extendTo` clamps ranges to.
 * @param {object} state
 * @param {string} instanceId
 * @returns {'shared' | 'page'}
 */
export function selectionGroupOf(state, instanceId) {
  return sharedTypeOf(state, instanceId) ? 'shared' : 'page';
}
